import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Pagination } from "@/components/Pagination";
import { ModeToggle, PhoneField } from "@/components/PhoneField";
import { adminService } from "@/services/adminService";
import { brandService } from "@/services/brandService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ADMIN_NAV } from "./nav";
import { ClientFilters, EMPTY_FILTERS, cleanFilters } from "@/components/ClientFilters";
import { ClientStatusCell } from "@/components/StatusBadge";
import { StatusFunnel } from "@/components/StatusFunnel";
import { ExportClientsButton } from "@/components/ExportClientsButton";
import { crmService } from "@/services/crmService";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white focus-visible:ring-[#2563EB]";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [managerFilter, setManagerFilter] = useState("");
  const [pageInfo, setPageInfo] = useState({ page: 1, per_page: 20, pages: 1, total: 0 });
  const [createOpen, setCreateOpen] = useState(false);
  const [balanceUser, setBalanceUser] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState("balance");
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", virtual_balance: 0, manager_id: "", brand_id: "", phone_dial: "+55", phone: "" });
  const [mode, setMode] = useState("email");
  const [amount, setAmount] = useState("250");
  const [managers, setManagers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [meta, setMeta] = useState(null);
  const [agents, setAgents] = useState([]);
  const [counts, setCounts] = useState([]);
  const activeBrands = useMemo(() => brands.filter((b) => b.active), [brands]);
  const managerName = (id) => managers.find((m) => m.id === id)?.name || "—";
  const managerOptions = useMemo(
    () => managers.filter((m) => m.id !== editUser?.id),
    [managers, editUser?.id],
  );

  useEffect(() => {
    adminService.listUsers({ role: "manager" }).then(setManagers).catch(() => {});
    adminService.listUsers({ role: "agent" }).then(setAgents).catch(() => {});
    brandService.list().then(setBrands).catch(() => {});
    crmService.meta().then(setMeta).catch(() => {});
  }, []);

  const loadCounts = useCallback(() => {
    crmService.statusCounts().then(setCounts).catch(() => setCounts([]));
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const load = useCallback(async () => {
    try {
      const params = { page: pageInfo.page, per_page: pageInfo.per_page, role: "user",
                       ...cleanFilters(filters) };
      if (search) params.search = search;
      if (managerFilter) params.manager_id = managerFilter;
      const data = await adminService.listUsers(params);
      setUsers(data.items);
      setPageInfo((prev) => ({ ...prev, page: data.page, pages: data.pages, total: data.total }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [search, managerFilter, filters, pageInfo.page, pageInfo.per_page]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser({
        name: form.name,
        password: form.password,
        role: "user",
        manager_id: form.manager_id || null,
        brand_id: form.brand_id || null,
        virtual_balance: Number(form.virtual_balance) || 0,
        ...(mode === "phone" ? { phone_dial: form.phone_dial, phone: form.phone } : { email: form.email }),
      });
      toast.success("Utilizador criado");
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", role: "user", virtual_balance: 0, manager_id: "", brand_id: "", phone_dial: "+55", phone: "" });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const addBalance = async (e) => {
    e.preventDefault();
    try {
      await adminService.adjustBalance(balanceUser.id, Number(amount), null, balanceTarget);
      toast.success(balanceTarget === "credit" ? "Crédito atualizado" : "Saldo atualizado");
      setBalanceUser(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await adminService.updateUser(editUser.id, {
        name: editUser.name,
        status: editUser.status,
        manager_id: editUser.manager_id || "",
        brand_id: editUser.brand_id ?? "",
      });
      toast.success("Utilizador atualizado");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remover definitivamente ${u.name}?`)) return;
    try {
      await adminService.deleteUser(u.id);
      toast.success("Utilizador removido");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Clientes" subtitle="Clientes da plataforma">
      <div data-testid="admin-users-page" className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4B5563]" />
            <Input
              data-testid="users-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Procurar por nome, email ou telefone"
              className={`${field} pl-10`}
            />
          </div>
          <select
            data-testid="users-manager-filter"
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            className="h-11 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white"
          >
            <option value="">Todos os gestores</option>
            <option value="none">Sem gestor</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <ExportClientsButton />
          <Button
            data-testid="create-user-button"
            onClick={() => setCreateOpen(true)}
            className="h-11 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] transition-colors hover:bg-[#1D4ED8]"
          >
            <Plus className="mr-2 h-4 w-4" /> Novo cliente
          </Button>
        </div>

        <StatusFunnel counts={counts} activeStatus={filters.crm_status}
                      onSelect={(slug) => { setFilters({ ...filters, crm_status: slug });
                                            setPageInfo((p) => ({ ...p, page: 1 })); }} />

        <ClientFilters meta={meta} agents={agents} filters={filters}
                       onChange={(f) => { setFilters(f); setPageInfo((p) => ({ ...p, page: 1 })); }}
                       onReset={() => setFilters(EMPTY_FILTERS)} />

        <div className="overflow-x-auto rounded-md border border-[#22252F] bg-[#12141A]">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-6 py-4">Nome</th>
                <th className="px-6 py-4">Status comercial</th>
                <th className="px-6 py-4">Gestor</th>
                <th className="px-6 py-4">Estado</th>
                <th className="px-6 py-4 text-right">Saldo</th>
                <th className="px-6 py-4">Criado</th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#1A1D25] transition-colors hover:bg-[#151821]">
                  <td className="px-6 py-4 text-white">
                    {u.name}
                  </td>
                  <td className="px-6 py-4"><ClientStatusCell client={u} testId={`user-status-${u.id}`} /></td>
                  <td data-testid={`manager-cell-${u.email}`} className="px-6 py-4 text-xs text-[#94A3B8]">
                    {u.role === "user" ? managerName(u.manager_id) : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-mono text-xs uppercase ${u.status === "active" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-mono text-white">{fmtMoney(u.virtual_balance)}</td>
                  <td className="px-6 py-4 font-mono text-xs text-[#94A3B8]">{fmtDate(u.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`add-balance-${u.id}`} onClick={() => { setBalanceUser(u); setAmount("250"); setBalanceTarget("balance"); }}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#10B981]/50 hover:text-[#10B981]">
                        <Wallet className="h-4 w-4" />
                      </button>
                      <Link to={`/clients/${u.id}`} data-testid={`profile-user-${u.id}`}
                            className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                        Perfil
                      </Link>
                      <button data-testid={`edit-user-${u.id}`} onClick={() => setEditUser({ ...u })}
                              className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                        Editar
                      </button>
                      <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-[#64748B]">Nenhum utilizador encontrado</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Pagination
          testId="users-pagination"
          page={pageInfo.page}
          pages={pageInfo.pages}
          total={pageInfo.total}
          perPage={pageInfo.per_page}
          onPage={(p) => setPageInfo((prev) => ({ ...prev, page: p }))}
          onPerPage={(n) => setPageInfo((prev) => ({ ...prev, per_page: n, page: 1 }))}
        />
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Novo cliente</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Nome</Label>
              <Input required data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} /></div>
            <ModeToggle mode={mode} onChange={setMode} testIdPrefix="new-user" />
            {mode === "phone" ? (
              <div className="space-y-2"><Label className={labelCls}>Telefone</Label>
                <PhoneField required dial={form.phone_dial} phone={form.phone}
                            onDial={(v) => setForm({ ...form, phone_dial: v })}
                            onPhone={(v) => setForm({ ...form, phone: v })}
                            testIdPrefix="new-user-phone" /></div>
            ) : (
            <div className="space-y-2"><Label className={labelCls}>Email</Label>
              <Input required type="email" data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} /></div>
            )}
            <div className="space-y-2"><Label className={labelCls}>Palavra-passe</Label>
              <Input required minLength={6} type="password" data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Saldo inicial</Label>
                <Input type="number" step="0.01" data-testid="new-user-balance" value={form.virtual_balance}
                       onChange={(e) => setForm({ ...form, virtual_balance: e.target.value })} className={`${field} font-mono`} /></div>
            </div>
            <div className="space-y-2"><Label className={labelCls}>Brand (obrigatória para Supervisor, fixa depois de criar)</Label>              <select data-testid="new-user-brand" value={form.brand_id}
                      onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                <option value="">Herdar do gestor / Meridian</option>
                {activeBrands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select></div>
            <div className="space-y-2"><Label className={labelCls}>Gestor responsável</Label>
              <select data-testid="new-user-manager" value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                <option value="">Sem gestor</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
              </select></div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-new-user" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!balanceUser} onOpenChange={(o) => !o && setBalanceUser(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="balance-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Adicionar saldo</DialogTitle></DialogHeader>
          <form onSubmit={addBalance} className="space-y-4">
            <p className="text-sm text-[#94A3B8]">{balanceUser?.email} · atual {fmtMoney(balanceUser?.virtual_balance)}</p>
            <div className="space-y-2"><Label className={labelCls}>Destino</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{ k: "balance", l: "Saldo" }, { k: "credit", l: "Crédito" }].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    data-testid={`balance-target-${o.k}`}
                    onClick={() => setBalanceTarget(o.k)}
                    className={`h-11 rounded-md border text-sm transition-colors ${
                      balanceTarget === o.k
                        ? "border-[#2563EB] bg-[#2563EB]/15 text-white"
                        : "border-[#22252F] text-[#94A3B8] hover:text-white"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label className={labelCls}>Montante (use negativo para retirar)</Label>
              <Input type="number" step="0.01" required data-testid="balance-amount-input" value={amount}
                     onChange={(e) => setAmount(e.target.value)} className={`${field} font-mono`} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-balance" className="rounded-md bg-[#10B981] text-[#04150F] hover:bg-[#0EA271]">Aplicar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Editar utilizador</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Nome</Label>
              <Input data-testid="edit-user-name" value={editUser?.name || ""} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className={field} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Estado</Label>
                <select data-testid="edit-user-status" value={editUser?.status || "active"} onChange={(e) => setEditUser({ ...editUser, status: e.target.value })}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  <option value="active">active</option><option value="suspended">suspended</option>
                </select></div>
            </div>
            <div className="space-y-2"><Label className={labelCls}>Brand (White Label)</Label>
              <select data-testid="edit-user-brand" value={editUser?.brand_id || ""}
                      onChange={(e) => setEditUser({ ...editUser, brand_id: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                <option value="">Herdar do gestor / Meridian</option>
                {activeBrands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select></div>

            <div className="space-y-2"><Label className={labelCls}>Gestor responsável</Label>
              <select data-testid="edit-user-manager" value={editUser?.manager_id || ""}
                      onChange={(e) => setEditUser({ ...editUser, manager_id: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                <option value="">Sem gestor</option>
                {managerOptions.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} · {m.email}</option>
                ))}
              </select></div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-edit-user" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
